import { useMutation } from '@apollo/client'
import { ReactElement, useEffect } from 'react'
import { useIntl, defineMessages, FormattedMessage } from 'react-intl'

import FormLayout from 'components/v2/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { useLogin } from 'features/Login/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { IPaymentSummariesFormValues } from 'features/PaymentsSummary/types'
import { CREATE_PAYMENT_SUMMARY } from 'features/PaymentsSummary/mutations'
import { NewPaymentSummaries } from 'features/PaymentsSummary/components/NewPaymentSummaries'
import Heading from 'components/v2/Heading'
import { useNavigate } from 'react-router-dom'
import { createPaymentSummaryFormValues } from 'features/PaymentsSummary/models'

const messages = defineMessages({
  helmet: {
    id: 'paymentSummary.new.helmet',
    defaultMessage: 'New Payment Summary | Nexbus Admin'
  }
})

const NewPaymentSummaryView = (): ReactElement => {
  const intl = useIntl()
  const navigate = useNavigate()
  const login = useLogin()

  useEffect((): void => {
    document.title = intl.formatMessage(messages.helmet)
  }, [intl])
  const [createPaymentSummaries, { loading, error }] = useMutation(CREATE_PAYMENT_SUMMARY, {
    onCompleted: () => {
      navigate('/partners/payment-summary')
    },
    onError: () => undefined
  })

  if (!canWrite(FEATURES.PARTNERS_PAYMENT_SUMMARY, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.PARTNERS_PAYMENT_SUMMARY} />
  }

  const handleSubmit = async (values: IPaymentSummariesFormValues): Promise<void> => {
    const readyDocuments = values.files.filter((file) => !!(file.file && file.path))

    try {
      await createPaymentSummaries({
        variables: {
          input: {
            name: values.name,
            files: readyDocuments.map((file) => ({
              category: file.category,
              filePath: file.path as string
            }))
          }
        }
      })
    } catch (error) {
      // Do nothing
    }
  }

  return (
    <FormLayout.Container>
      <FormLayout.Content isSmallContent>
        <Heading.H3 capitalize>
          <FormattedMessage id="paymentSummary.new.title" defaultMessage="Payment Summary" />
        </Heading.H3>
        <NewPaymentSummaries
          initialValues={createPaymentSummaryFormValues()}
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
        />
      </FormLayout.Content>
    </FormLayout.Container>
  )
}

export default withLastUrl(NewPaymentSummaryView)
